import { saveStateWithAjax, getPackageImageSource, checkSubmit, checkKey } from './screen-package-selection-global'

/* ============================================================
   RULE CONFIG
============================================================ */

// DEBUG: Expose functions to DevTools console
window.__RS5__ = window.__RS5__ || {}
window.__RS5__.saveStateWithAjax = saveStateWithAjax
window.__RS5__.checkSubmit = checkSubmit
window.__RS5__.getPackageImageSource = getPackageImageSource

const DEBUG_RS5 = false
function dbg(...args) {
  if (DEBUG_RS5) {
    console.log('[RS5]', ...args)
  }
}

const BASE_COLOR_VALUE_BY_MODE = {
  PWX_A5: '199',
  PWY_A5: '98'
}

const BASE_COLOR_VALUES = Object.values(BASE_COLOR_VALUE_BY_MODE)

const rs5Memory = {
  baseColorByMode: {
    PWX_A5: BASE_COLOR_VALUE_BY_MODE.PWX_A5,
    PWY_A5: BASE_COLOR_VALUE_BY_MODE.PWY_A5
  }
}

const persistQueue = {
  runId: 0
}

const RULES_RS5_A5 = {
  modeGroup: ['PWX_A5', 'PWY_A5'],

  modes: {
    /* ======================= PWX ======================= */
    PWX_A5: {
      defaultOnFirstLoad: true,
      rules: [
        { type: 'disableTargets', ifOn: 'FC1_A5', disable: ['YN3_A5', 'YN4_A5', 'YFB_A5'] },
        { type: 'disableTargets', ifOn: 'Q8H_A5', disable: ['YN3_A5', 'YN4_A5', 'YFB_A5'] },
        { type: 'disableTargets', ifOn: '0UD_RS5', disable: ['YN3_A5', 'YN4_A5', 'YFB_A5'] },
        { type: 'disableTargets', ifOn: '0TF_A5', disable: ['YN3_A5', 'YN4_A5', 'YFB_A5'] },
        { type: 'disableTargets', ifOn: 'FZ8_A5', disable: ['FZ9_A5'] },
        { type: 'disableTargets', ifOn: 'FZ9_A5', disable: ['FZ8_A5'] },
        { type: 'disableTargets', ifOn: 'YFA_A5', disable: ['YN3_A5', 'YN4_A5', 'YFB_A5'] },
        { type: 'disableTargets', ifOn: 'YFB_A5', disable: ['FC1_A5', '0UD_RS5', 'Q8H_A5', 'YFA_A5', '0TF_A5'] },

        { type: 'requires', ifOn: 'YFA_A5', thenOn: ['FC1_A5', 'Q8H_A5', '0UD_RS5', '0TF_A5'] },

        { type: 'excludes', a: 'YN3_A5', b: 'YN4_A5' },

        { type: 'allowOnly', ifOn: 'YN3_A5', allow: ['YFB_A5', '7TZ_A5', '7TN_A5', 'FZ8_A5', 'FZ9_A5'] },
        { type: 'allowOnly', ifOn: 'YN4_A5', allow: ['YFB_A5', '7TZ_A5', '7TN_A5', 'FZ8_A5', 'FZ9_A5'] },
        { type: 'allowOnly', ifOn: 'YFB_A5', allow: ['YN3_A5', 'YN4_A5', '7TZ_A5', '7TN_A5', 'FZ8_A5', 'FZ9_A5'] }
      ]
    },

    /* ======================= PWY ======================= */
    PWY_A5: {
      disableMode: ['FC1_A5', 'YN3_A5', 'YN4_A5', 'YFA_A5', 'YFB_A5'],
      rules: [
        { type: 'disableTargets', ifOn: 'FZ8_A5', disable: ['FZ9_A5'] },
        { type: 'disableTargets', ifOn: 'FZ9_A5', disable: ['FZ8_A5'] }
      ]
    }
  }
}

/* ============================================================
   DOM HELPERS
============================================================ */

function getLabelFor(mapVal, key) {
  if (mapVal?.tagName === 'LABEL') return mapVal
  if (mapVal?.tagName === 'INPUT') return mapVal.closest('label[data-auicode]')
  return document.querySelector(`label[data-auicode="${key}"]`)
}

function getInputFor(mapVal, key) {
  if (mapVal?.tagName === 'INPUT') return mapVal
  if (mapVal?.tagName === 'LABEL') return mapVal.querySelector('input')
  return document.querySelector(`label[data-auicode="${key}"] input`)
}

function setChecked(map, key, checked) {
  const input = getInputFor(map[key], key)
  const label = getLabelFor(map[key], key)
  if (!input) return

  input.checked = checked
  label?.classList.toggle('is-checked', checked)
}

function setDisabled(map, key, disabled) {
  const input = getInputFor(map[key], key)
  if (input) input.disabled = disabled
}

function setForcedBadge(map, key, forced) {
  const label = getLabelFor(map[key], key)
  label?.classList.toggle('is-forced', forced)
}

function isSelected(map, id) {
  const input = getInputFor(map[id], id)
  const label = getLabelFor(map[id], id)
  if (!input) return false
  return !!input.checked || !!label?.classList?.contains('is-checked')
}

function snapshotState(map) {
  const state = {}
  for (const id of Object.keys(map)) {
    state[id] = isSelected(map, id)
  }
  return state
}

function getAllInputsFromMap(map) {
  return Object.keys(map)
    .map(key => getInputFor(map[key], key))
    .filter(Boolean)
}

function getLabelForInput(input) {
  return input ? input.closest('label[data-auicode]') : null
}

/* ============================================================
   BASE COLORS (BY INPUT VALUE)
============================================================ */

function getCurrentMode(map) {
  return RULES_RS5_A5.modeGroup.find(code => isSelected(map, code)) || null
}

function isBaseColorValue(value) {
  return BASE_COLOR_VALUES.includes(String(value))
}

function findInputByValue(map, value) {
  const wanted = String(value)
  return getAllInputsFromMap(map).find(input => String(input.value) === wanted) || null
}

function getSelectedBaseColorValue(map) {
  const selected = getAllInputsFromMap(map).find(input => {
    return input.checked && isBaseColorValue(input.value)
  })
  return selected ? String(selected.value) : null
}

function rememberSpecificModeBaseColor(mode, colorValue) {
  if (!mode || colorValue == null) return
  rs5Memory.baseColorByMode[mode] = String(colorValue)
  dbg('rememberSpecificModeBaseColor', { mode, colorValue: String(colorValue) })
}

function setExclusiveBaseColorByValue(map, colorValue) {
  const target = String(colorValue)

  getAllInputsFromMap(map).forEach(input => {
    if (!isBaseColorValue(input.value)) return

    const checked = String(input.value) === target
    input.checked = checked

    const label = getLabelForInput(input)
    label?.classList.toggle('is-checked', checked)
  })

  dbg('setExclusiveBaseColorByValue', target)
}

function applyRememberedOrDefaultBaseColor(map, mode) {
  if (!mode) return

  const remembered = rs5Memory.baseColorByMode[mode]
  const fallback = BASE_COLOR_VALUE_BY_MODE[mode]
  const targetValue = String(remembered || fallback || '')

  if (!targetValue) return

  const targetInput = findInputByValue(map, targetValue)
  if (!targetInput) {
    dbg('applyRememberedOrDefaultBaseColor skipped, value missing in DOM', { mode, targetValue })
    return
  }

  setExclusiveBaseColorByValue(map, targetValue)
  dbg('applyRememberedOrDefaultBaseColor', { mode, targetValue })
}

function enforceActiveModeBaseColor(map) {
  const currentMode = getCurrentMode(map)
  if (!currentMode) return
  applyRememberedOrDefaultBaseColor(map, currentMode)
}

/* ============================================================
   PERSIST
============================================================ */

function persistAllStates(map) {
  const allIds = Object.keys(map)

  const priorityIds = [
    'PWX_A5',
    'PWY_A5',
    'YN3_A5',
    'YN4_A5',
    'YFB_A5',
    'YFA_A5',
    'FC1_A5',
    'Q8H_A5',
    '0UD_RS5',
    '0TF_A5',
    'FZ8_A5',
    'FZ9_A5'
  ].filter(id => allIds.includes(id))

  const priorityFalse = priorityIds.filter(id => !isSelected(map, id))
  const priorityTrue = priorityIds.filter(id => isSelected(map, id))
  const rest = allIds.filter(id => !priorityIds.includes(id))

  const orderedIds = [...priorityFalse, ...priorityTrue, ...rest]

  const currentRunId = ++persistQueue.runId
  const delayMs = 35

  function sendNext(index) {
    if (currentRunId !== persistQueue.runId) {
      dbg('persist aborted by newer run')
      return
    }

    if (index >= orderedIds.length) {
      dbg('persist finished', orderedIds)
      return
    }

    const id = orderedIds[index]
    const checked = isSelected(map, id)

    dbg('persist', index, id, checked)
    saveStateWithAjax(id, checked)

    setTimeout(() => {
      sendNext(index + 1)
    }, delayMs)
  }

  sendNext(0)
}

/* ============================================================
   NORMALIZE / SPECIAL
============================================================ */

function normalizeSelected(selected, modeCfg, options = {}) {
  const { initial = false } = options
  if (!initial) return
}

function applySpecialBackPropagation_rs5(map, changedId) {
  if (changedId !== 'YFA_A5') return

  const yfaOn = isSelected(map, 'YFA_A5')
  const linked = ['FC1_A5', 'Q8H_A5', '0UD_RS5', '0TF_A5']

  linked.forEach(id => {
    setChecked(map, id, yfaOn)
  })
}

function updateSubmitButton_rs5(frontCheckboxesMap, options = {}) {
  const { initial = false } = options

  const btn = document.getElementById('navigation-submit-btn')
  if (!btn) return

  const ids = Object.keys(frontCheckboxesMap)
  const modeSet = new Set(RULES_RS5_A5.modeGroup || [])

  const selected = ids.filter(id => !modeSet.has(id) && isSelected(frontCheckboxesMap, id))

  if (initial) {
    btn.disabled = true
    return
  }

  if (selected.length === 0) {
    btn.disabled = true
    return
  }

  const yfbOn = isSelected(frontCheckboxesMap, 'YFB_A5')
  if (yfbOn) {
    const hasYn3OrYn4 = isSelected(frontCheckboxesMap, 'YN3_A5') || isSelected(frontCheckboxesMap, 'YN4_A5')
    btn.disabled = !hasYn3OrYn4
    return
  }

  btn.disabled = false
}

/* ============================================================
   ENGINE
============================================================ */

function ensureMode(selected, config, ctx) {
  const modes = config.modeGroup

  if (ctx?.lastClicked && modes.includes(ctx.lastClicked)) {
    selected.add(ctx.lastClicked)
    for (const id of modes) {
      if (id !== ctx.lastClicked) selected.delete(id)
    }
    return ctx.lastClicked
  }

  let active = modes.find(id => selected.has(id))

  if (!active) {
    const def = modes.find(id => config.modes[id]?.defaultOnFirstLoad)
    active = def || modes[0]
    selected.add(active)
  }

  for (const id of modes) {
    if (id !== active) selected.delete(id)
  }

  return active
}

function resolveExclude(a, b, ctx, forced) {
  if (forced.has(a)) return b
  if (forced.has(b)) return a
  if (ctx.lastClicked === a) return b
  if (ctx.lastClicked === b) return a
  return b
}

function computeState(map, config, ctx, options = {}) {
  const ids = Object.keys(map)
  const selected = new Set()

  const snapshot = {}
  for (const id of ids) {
    snapshot[id] = isSelected(map, id)
    if (snapshot[id]) selected.add(id)
  }

  dbg('DOM STATE BEFORE COMPUTE', snapshot)

  const disabled = new Set()
  const forced = new Set()

  const mode = ensureMode(selected, config, ctx)
  const modeCfg = config.modes[mode] || {}

  normalizeSelected(selected, modeCfg, options)

  dbg('Active mode:', mode)
  dbg('Selected after normalize:', [...selected])

  for (const id of (modeCfg.disableMode || [])) {
    disabled.add(id)
    selected.delete(id)
  }

  let changed = true
  let guard = 0

  while (changed && guard++ < 50) {
    changed = false

    for (const r of (modeCfg.rules || [])) {
      if (r.type === 'disableTargets' && selected.has(r.ifOn)) {
        for (const t of (r.disable || [])) {
          if (!disabled.has(t)) {
            disabled.add(t)
            changed = true
          }
          if (selected.delete(t)) {
            changed = true
          }
        }
      }
    }

    const activeAllowOnly = (modeCfg.rules || []).filter(r => r.type === 'allowOnly' && selected.has(r.ifOn))
    if (activeAllowOnly.length) {
      let allowed = new Set(ids)

      for (const r of activeAllowOnly) {
        const thisAllowed = new Set([...config.modeGroup, r.ifOn, ...(r.allow || [])])
        allowed = new Set([...allowed].filter(x => thisAllowed.has(x)))
      }

      for (const id of ids) {
        if (!allowed.has(id)) {
          if (!disabled.has(id)) {
            disabled.add(id)
            changed = true
          }
          if (selected.delete(id)) {
            changed = true
          }
        }
      }
    }

    for (const r of (modeCfg.rules || [])) {
      if (r.type === 'requires' && selected.has(r.ifOn)) {
        for (const dep of (r.thenOn || [])) {
          if (disabled.has(dep)) {
            disabled.delete(dep)
            changed = true
          }
          if (!selected.has(dep)) {
            selected.add(dep)
            changed = true
          }
          if (!forced.has(dep)) {
            forced.add(dep)
            changed = true
          }
        }
      }
    }

    for (const r of (modeCfg.rules || [])) {
      if (r.type === 'excludes' && selected.has(r.a) && selected.has(r.b)) {
        const drop = resolveExclude(r.a, r.b, ctx, forced)
        if (selected.delete(drop)) {
          changed = true
        }
      }
    }

    for (const id of disabled) {
      if (selected.delete(id)) {
        changed = true
      }
    }
  }

  for (const id of config.modeGroup) {
    forced.delete(id)
  }

  dbg('FINAL STATE', {
    selected: [...selected],
    disabled: [...disabled],
    forced: [...forced]
  })

  return { selected, disabled, forced }
}

function applyState(map, state, options = {}) {
  dbg('APPLY STATE', {
    selected: [...state.selected],
    disabled: [...state.disabled],
    forced: [...state.forced],
    options
  })

  for (const id of Object.keys(map)) {
    const shouldCheck = state.selected.has(id)
    const shouldDisable = state.disabled.has(id) || state.forced.has(id)

    setChecked(map, id, shouldCheck)
    setDisabled(map, id, shouldDisable)
    setForcedBadge(map, id, state.forced.has(id))
  }

  enforceActiveModeBaseColor(map)

  updateBackgroundImage()
  getImages_rs5(map)
  checkSubmit(map)
  updateSubmitButton_rs5(map, options)
}

function createEngine(config) {
  const ctx = { lastClicked: null }

  function recompute(map, changedId = null, options = {}) {
    if (changedId) {
      ctx.lastClicked = changedId
    }

    dbg('RECOMPUTE', { changedId, options })

    const state = computeState(map, config, ctx, options)
    applyState(map, state, options)
    return state
  }

  return { recompute }
}

/* ============================================================
   LISTENERS
============================================================ */

function addListeners_rs5(frontCheckboxesMap, modalsCheckboxesMap, config = RULES_RS5_A5) {
  try {
    if (typeof checkKey === 'function') {
      checkKey(frontCheckboxesMap)
      dbg('checkKey initialized')
    } else {
      dbg('checkKey not available')
    }
  } catch (e) {
    console.error('[RS5] checkKey ERROR', e)
  }

  const engine = createEngine(config)
  const ids = Object.keys(frontCheckboxesMap)

  for (const id of ids) {
    const input = getInputFor(frontCheckboxesMap[id], id)
    if (!input) continue

    input.addEventListener('change', function () {
      setTimeout(() => {
        dbg('CHANGE start', id, snapshotState(frontCheckboxesMap))

        const isModeSwitch = config.modeGroup.includes(id)
        const selectedBaseColorValueBefore = getSelectedBaseColorValue(frontCheckboxesMap)

        if (selectedBaseColorValueBefore) {
          const currentModeBefore = getCurrentMode(frontCheckboxesMap)
          if (currentModeBefore) {
            rememberSpecificModeBaseColor(currentModeBefore, selectedBaseColorValueBefore)
          }
        }

        engine.recompute(frontCheckboxesMap, id, { initial: false })

        if (isModeSwitch) {
          applyRememberedOrDefaultBaseColor(frontCheckboxesMap, id)
        }

        applySpecialBackPropagation_rs5(frontCheckboxesMap, id)

        engine.recompute(frontCheckboxesMap, id, { initial: false })

        const selectedBaseColorValueAfter = getSelectedBaseColorValue(frontCheckboxesMap)
        const currentModeAfter = getCurrentMode(frontCheckboxesMap)
        if (currentModeAfter && selectedBaseColorValueAfter) {
          rememberSpecificModeBaseColor(currentModeAfter, selectedBaseColorValueAfter)
        }

        enforceActiveModeBaseColor(frontCheckboxesMap)
        persistAllStates(frontCheckboxesMap)

        checkSubmit(frontCheckboxesMap)
        updateSubmitButton_rs5(frontCheckboxesMap, { initial: false })

        dbg('CHANGE end', id, snapshotState(frontCheckboxesMap))
      }, 0)
    })
  }

  setTimeout(() => {
    dbg('INIT before', snapshotState(frontCheckboxesMap))

    engine.recompute(frontCheckboxesMap, null, { initial: true })

    const initialMode = getCurrentMode(frontCheckboxesMap) || 'PWX_A5'
    applyRememberedOrDefaultBaseColor(frontCheckboxesMap, initialMode)

    const initialBaseColorValue = getSelectedBaseColorValue(frontCheckboxesMap)
    if (initialBaseColorValue) {
      rememberSpecificModeBaseColor(initialMode, initialBaseColorValue)
    }

    checkSubmit(frontCheckboxesMap)
    updateSubmitButton_rs5(frontCheckboxesMap, { initial: false })

    dbg('INIT after', snapshotState(frontCheckboxesMap))
  }, 0)
}

/* ============================================================
   IMAGES
============================================================ */

function getImages_rs5(map) {
  for (const id in map) {
    getPackageImages_rs5(id, map)
  }
}

function getPackageImages_rs5(packageCode, map) {
  const input = getInputFor(map[packageCode], packageCode)
  if (!input) return

  const imageSource = getPackageImageSource(packageCode)
  let img = document.getElementById(packageCode + '-img')

  if (!img && input.checked) {
    const wrap = document.querySelector('.image-wrap')
    if (!wrap) return

    const newImg = document.createElement('IMG')
    newImg.classList.add('overlay')
    newImg.src = imageSource
    newImg.id = packageCode + '-img'
    wrap.appendChild(newImg)
    img = newImg
  }

  if (img) {
    img.hidden = !input.checked
  }
}

/* ============================================================
   BACKGROUND
============================================================ */

function updateBackgroundImage() {
  const pwx = document.querySelector('label[data-auicode="PWX_A5"] input')?.checked
  const pwy = document.querySelector('label[data-auicode="PWY_A5"] input')?.checked
  const fc1 = document.querySelector('label[data-auicode="FC1_A5"] input')?.checked

  const img = document.getElementById('front_background_image')
  if (!img) return

  if (pwy) {
    img.src = 'img/06_paketauswahl/a5/au401_rs5/au401_rs5_99y_paketauswahl.png'
  } else if (fc1) {
    img.src = 'img/06_paketauswahl/a5/au401_rs5/au401_rs5_fc1_paketauswahl.png'
  } else if (pwx) {
    img.src = 'img/06_paketauswahl/a5/au401_rs5/au401_rs5_99x_paketauswahl.png'
  } else {
    img.src = 'img/06_paketauswahl/a5/au401_rs5/au401_rs5_99x_paketauswahl.png'
  }
}

export {
  addListeners_rs5,
  getImages_rs5,
  getPackageImages_rs5
}